<template>
  <TabsContainer id="fr-details-container"
    :class="[
      'framework-details',
      tabContainerClassesStr,
      showProjGradDisclaimer?'is-proj-grad':'',
      isUpdatingLayout?'updating-layout':''
    ]"
    :is-calculating-layout="isUpdatingLayout"
    :input-tab-index="selectedTabIndex"
    tab-list-class="shadowed-bar no-print"
    tab-list-label="School Details"
    :tab-button-ids="tabButtonIds"
    :tab-content-ids="tabContentIds"
    :tab-button-classes="tabButtonClasses"
    :tab-content-classes="tabContentClasses"
    @tab-selected="onTabSelected"
    >
    <template v-for="(c,i) in tabCodes">
      <template :slot="`tab-button-${i}`">
        <span v-for="j in 2" :key="`btn-${i}-${j}`" class="tab-button-label">{{tabButtonLabelForTabCodeLine(c,j)}}</span>
      </template>
      <template :slot="`tab-print-header-${i}`">
        <div :class="['fr-print-header print-only',isPageTopCode(c)?'page-break-before':'',isUpdatingLayout?'calc-hidden':'']" :key="'ph-'+c">
          <div v-if="isPageTopCode(c)" class="school-name print-only">{{selectedSchoolDisplayName}}</div>
          <div class="mobile-view-spacer" :id="c"></div>
          <DivPrintableBackground :class="colorClassForCode(c)">
            <div class="section-name print-white">{{tabNameForTabCode(c)}}</div>
          </DivPrintableBackground>
        </div>
        <div :class="['fr-responsive-header no-print',colorClassForCode(c)]" :key="'rh-'+c">
          <div class="v-outer">
            <div class="v-middle content">
              <div class="section-name print-white">{{tabNameForTabCode(c)}}</div>
            </div>
          </div>
        </div>
      </template>

      <template :slot="`tab-content-${i}`">
        <div ref="scrollLocation" :class="['fr-tab-content',isPrintHideRRCode(c)?'print-hide-survey-rr':'']" :key="c">
          <ElementOverallRating
            v-if="c!=='INFO'"
            :element-code="c"
          />

          <div class="metric-columns-wrapper">
            <div v-for="colNum in layoutMetadataForTab(c)"
              :key="colNum.key"
              :class="['metric-column','col-'+colNum.key]"
            >
              <div v-for="group in colNum.values"
                :key="group.key"
                :class="['metric-group',group.key]"
              >
                <template v-for="r in group.values">
                  <component :key="r.key"
                    v-if="isValidComponentType(r.values[0].component_type)"
                    :is="r.values[0].component_type"
                    :metadata="r.values"
                    :is-calculating-layout="isUpdatingLayout"
                  />
                  <div v-else :key="r.key">Unknown component type: {{r.values[0].component_type}}</div>
                </template>
              </div>
            </div>
            <!-- This can be removed if the new survey-viz module will be a permanent addition -->
            <!-- Assumes TR section only has one column, duplicate footer here for printing only -->
            <!-- <div v-if="c=='TR'" class="metric-column col-2">
              <ReportFooter ref="print-footer" :class="['print-only',isUpdatingLayout?'calc-hidden':'']"/>
            </div> -->
          </div>
        </div>
      </template>
    </template>
  </TabsContainer>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

import { colorClassForCode } from '@/utils/data'
import { logEvent } from '@/helpers/google-analytics'
import { SQS_PROJ_GRAD_RATE_YEAR } from '@/report-settings'
import { REPORT_YEAR, NS_CURRENT_REPORT } from './const'

import TabsContainer from '@/components/TabsContainer'
import DivPrintableBackground from '@/components/DivPrintableBackground'
import ReportFooter from './ReportFooter'

import {
  ElementOverallRating,
  GroupHeading,
  SubHeading,
  Description,
  DescriptionNoSR,
  DescriptionSafe,
  MetricSchoolOverview,
  MetricDescColon,
  MetricBignum,
  MetricSmallnum,
  MetricSmallnumD75,
  MetricCharterAuthorizer,
  MetricSchoolMap,
  MetricRatingGroup,
  MetricRatingGroupPkComp,
  MetricRatingGrid,
  MetricSurveyRR,
  MetricValueList,
  MetricValueListCount,
  MetricValueListNotSafe,
  MetricVizCompBars,
  MetricVizCompBarsInfo,
  MetricVizCompBarsSurvey,
  MetricThenNow,
  MetricAltAssess
} from './tab-mods'

/* For custom layout of text in buttons to determine line breaks,
  join without space for regular text due to SF */
const frElementNameLookupByLine = {
  'INFO': ['School ', 'Info'],
  'SA': ['Student ', 'Achievement'],
  'RI': ['Rigorous ', 'Instruction'],
  'CT': ['Collaborative ', 'Teachers'],
  'SE': ['Supportive ', 'Environment'],
  'ES': ['Effective School ', 'Leadership'],
  'SF': ['Strong Family-', 'Community Ties'],
  'TR': ['Trust']
}

@Component({
  computed: {
    ...mapGetters(NS_CURRENT_REPORT, [
      'isEmbargoed',
      'selectedSchoolType',
      'isSelectedSchoolCharter',
      'isSelectedSchoolDistrictPK',
      'selectedSchoolData',
      'layoutMetadataForSchoolType',
      'selectedSchoolDisplayName',
      'hasQRReview'
    ]),
    ...mapGetters(NS_CURRENT_REPORT, {
      tabCodes: 'selectedSchoolTabCodes'
    })
  },
  methods: {
    colorClassForCode
  },
  components: {
    TabsContainer,
    DivPrintableBackground,
    ReportFooter,
    ElementOverallRating,
    GroupHeading,
    SubHeading,
    Description,
    DescriptionNoSR,
    DescriptionSafe,
    MetricSchoolOverview,
    MetricDescColon,
    MetricBignum,
    MetricSmallnum,
    MetricSmallnumD75,
    MetricCharterAuthorizer,
    MetricSchoolMap,
    MetricRatingGroup,
    MetricRatingGroupPkComp,
    MetricRatingGrid,
    MetricSurveyRR,
    MetricValueList,
    MetricValueListCount,
    MetricValueListNotSafe,
    MetricVizCompBars,
    MetricVizCompBarsInfo,
    MetricVizCompBarsSurvey,
    MetricThenNow,
    MetricAltAssess
  }
})
export default class FrameworkDetails extends Vue {
  loading = true // true on startup, or when layout changes
  changingSchool = false
  isProjectedGradRate = REPORT_YEAR === SQS_PROJ_GRAD_RATE_YEAR

  get pageTopCodes () {
    switch (this.selectedSchoolType) {
      case 'HS':
      case 'MS':
      case 'K-8':
      case 'HST':
        return ['SA', 'RI', 'CT', 'SE', 'SF', 'TR']
      case 'D75':
        return ['SA', 'RI', 'CT', 'SE', 'ES', 'SF', 'TR']
      case 'ES':
      case 'EC':
        return ['SA', 'RI', 'SE', 'ES', 'SF', 'TR']
      default:
        return ['SA', 'RI', 'SE', 'SF', 'TR'] // Default is for section breaks for Pre-K school types
    }
  }
  get printHideRRCodes () {
    switch (this.selectedSchoolType) {
      case 'HS':
      case 'MS':
      case 'K-8':
      case 'HST':
        return ['SE']
      case 'D75':
        return []
      case 'ES':
      case 'EC':
        return ['RI']
      default:
        return ['RI', 'SE', 'SF'] // Original list of sections to hide survey response rates when it was for all school types
        // Changed CSS logic so opposite from original list
    }
  }
  selectedTabCode = '' // invalid tab will default to first tab

  /* Lifecycle */

  loadTabHash () {
    const hashString = window.location.hash.substr(1)
    const hashIndex = this.tabCodes.indexOf(hashString)
    if (hashIndex > -1) {
      this.selectedTabCode = hashString
    }
  }

  mounted () {
    this.loading = false
    this.loadTabHash()
    window.onhashchange = this.loadTabHash
    this.trackMetaLinks()
  }

  updated () {
    /* Removed as part of the removal of the hidden footer second-column in Trust section */
    // if (this.changingSchool) {
    //   /* Only update size of hidden footer for printing when data changes,
    //     not when visibility changes */
    //   this.$refs['print-footer'][0].updateBGSize()
    // }
    this.loading = false
    this.changingSchool = false
    this.trackMetaLinks()
  }

  /* Watchers */

  @Watch('layoutMetadata')
  onLayoutMetadataChange (newVal, oldVal) {
    this.loading = true
  }
  @Watch('selectedSchoolData')
  onSchoolDataChange (newVal, oldVal) {
    this.changingSchool = true
  }

  /* Getters */

  get showProjGradDisclaimer () {
    // No need to show disclaimer for unofficial since grad rate is suppressed
    return !this.isEmbargoed && this.isProjectedGradRate
  }

  get selectedTabIndex () {
    // Default to first tab in case selected tab (e.g. SA) is no longer valid
    //  for newly selected school type
    const tabIndex = this.tabCodes.indexOf(this.selectedTabCode)
    return tabIndex > -1 ? tabIndex : 0
  }

  get isUpdatingLayout () { return this.loading || this.changingSchool }

  get tabContainerClassesStr () {
    const classes = []
    if (this.selectedSchoolType) classes.push('st-' + this.selectedSchoolType.toLowerCase())
    if (this.isSelectedSchoolDistrictPK) classes.push('pk-district')
    if (this.isSelectedSchoolCharter) classes.push('charter')
    if (!this.hasQRReview) classes.push('no-qr')
    return classes.join(' ')
  }

  get tabButtonIds () { return this.tabCodes.map(c => this.tabButtonIdForCode(c)) }
  get tabContentIds () { return this.tabCodes.map(c => this.tabContentIdForCode(c)) }
  get tabButtonClasses () { return this.tabCodes.map(c => colorClassForCode(c)) }
  get tabContentClasses () { return this.tabCodes.map(c => 'print-always') }

  get layoutMetadata () {
    return this.layoutMetadataForSchoolType(this.selectedSchoolType)
  }
  get validComponentTypes () {
    let r = {}
    Object.assign(r, this.$options.components)
    return r
  }

  /* Event Handlers */

  onTabSelected (i) {
    this.selectedTabCode = this.tabCodes[i]
    window.location.hash = this.tabCodes[i]
    // scrolls the screen to be at the beginning of Tab content when you are past the content
    Vue.nextTick().then(() => {
      const element = this.$refs['scrollLocation'][i]
      // 125 is to move under the sticky top menu
      const top = element.offsetTop - 125
      if (top - window.scrollY < 0) {
        window.scrollTo(0, top)
      }
    })
  }

  /* Methods - utility */

  trackMetaLinks () {
    var links = document.querySelectorAll('.meta-link')
    links = Array.prototype.slice.call(links)
    links.forEach(function (e) {
      e.onclick = function () {
        logEvent(Vue, 'Link', 'linkClicked', e.href)
      }
    })
  }

  isValidComponentType (t) {
    return this.validComponentTypes.hasOwnProperty(t)
  }
  tabNameForTabCode (c) {
    const lines = frElementNameLookupByLine[c]
    return lines ? lines.join('') : ''
  }
  tabButtonLabelForTabCodeLine (c, i) {
    // i is line number indexed beginning with 1
    const lines = frElementNameLookupByLine[c]
    return lines && lines.length >= i ? lines[i - 1] : ''
  }
  tabButtonIdForCode (c) { return 'tab-button-' + c.toLowerCase() }
  tabContentIdForCode (c) { return 'tab-content-' + c.toLowerCase() }
  layoutMetadataForTab (c) { return this.layoutMetadata[c] || {} }
  isPageTopCode (c) { return this.pageTopCodes.indexOf(c) > -1 }
  isPrintHideRRCode (c) { return this.printHideRRCodes.indexOf(c) > -1 }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import './css/const.scss';
$info-desc-indent: 16px;
.framework-details {
  background-color: white;
  box-shadow: 0 4px 5px #bbbbbb;
  margin-bottom: 15px;
  /deep/ .tab-button-bar {
    justify-content: space-between;
    position: sticky;
    top: 46px;
    z-index: 1;
  }
  /deep/ .tab-button {
    flex: 0 1 auto;
    font-size: 15px;
    padding: 8px 3px 10px 3px;
    border-width: 4px 0px 0px 0px;
    border-style: solid;
    background-color: white;
    box-shadow: 0px 1px 0px white;
    &:not(.selected):hover {
      color: #333;
      box-shadow: none;
    }
    &:not(.selected):not(:hover) {
      border-color: transparent;
      background-color: transparent;
      box-shadow: none;
    }
  }
  /deep/ .tab-content {
    padding: 0px 0px 10px 0px;
    margin: 0px 24px 0px 24px;
    @media print {
      padding-bottom: 0px;
    }
  }
  /deep/ .tab-print-header {
    padding: 0px;
    .school-name {
      font-size: 12px;
      padding: 8px;
    }
    .div-printable-bg {
      height: 48px;
      border-radius: 8px;
    }
  }
}
.color-info {
  background-color: #777777;
  border-top-color: #777777;
}
.section-name {
  width: 100%;
  padding: 0px 10px;
  font-size: 24px;
  font-weight: bold;
  color: white;

  /* This fixes name sticking out to right, but printing no longer fits? */
  box-sizing: border-box;
}
.fr-responsive-header {
  height: 34px;
  border-radius: 8px;
  .section-name {
    font-size: 16px;
    padding: 0px 10px;
  }
}
.tab-button-label {
  display: block;
  padding-left: 10px;
  padding-right: 10px;
}

.metric-columns-wrapper {
  display: flex;
  justify-content: space-between;
}
.metric-column {
  /* Fixed 24px between two columns */
  width: calc(50% - 12px);
  min-width: $min-width-col;
}

/* Customization of behavior sub-components, including based on metric group */

.framework-details.charter {
  #tab-content-info {
    .metric-group.adv_courses {
      &::after {
        display: block;
        content: "Advanced Course data not available for charter schools.";
      }
      .metric-value-list.no-values {
        display: none;
      }
    }
    .metric-group.adv-demog {
      display: none;
    }
  }
}
#tab-content-info {
  .metric-group.gen .description,
  .metric-group.sports .description.metric-colon {
    padding-left: 2*$info-desc-indent;
    text-indent: -$info-desc-indent;
  }
  .metric-group.gen,
  .metric-group.sports {
    .sub-heading {
      padding-top: 20px;
    }
    .metric-bignum {
      margin-bottom: 0px;
    }
  }
  .metric-group.sports {
    .group-heading + .sub-heading {
      padding-top: 0;
    }
    .description.metric-colon {
      word-break: break-word;
    }
  }
  .metric-group.adv_courses {
    .metric-value-list {
      /deep/ .value-row {
        .value {
          width: 100px;
        }
      }
    }
  }
  .metric-group[class*="perf"] {
    /* For D75 */
    .description {
      margin-bottom: 16px;
    }
  }
  .metric-group.sch-demog, .metric-group.adv-demog {
    /deep/ .viz-comp-bars {
      .bar.school-all {
        fill: #1F497D;
      }
    }
    /deep/ .viz-legend {
      .legend-group .bar.school-all {
          border-left-color: #1F497D;
      }
    }
  }
  .metric-group.sch-demog {
    /deep/ .viz-comp-bars {
      .bar.boro-dist {
        fill: #437157;
      }
      .bar.neighborhood {
        fill: #399DAE;
      }
    }
    /deep/ .viz-legend {
      .legend-group .bar.boro-dist {
          border-left-color: #437157;
      }
    }
    /deep/ .viz-legend {
      .legend-group .bar.neighborhood {
          border-left-color: #399DAE;
      }
    }
  }
  .metric-group.adv-demog {
    /deep/ .viz-comp-bars {
      .bar.adv-courses {
        fill: #876B8A;
      }
      .bar.ap-ib-courses {
        fill: #6898B6;
      }
    }
    /deep/ .viz-legend {
      .legend-group .bar.adv-courses {
          border-left-color: #876B8A;
      }
    }
    /deep/ .viz-legend {
      .legend-group .bar.ap-ib-courses {
          border-left-color: #6898B6;
      }
    }
  }
  .metric-group:not(.sports) {
    page-break-inside: avoid;
  }
}

#tab-content-sa {
  .metric-group.grad /deep/ .grad-rate-disclaimer {
    display: inline-block;
    font-style: italic;
    padding-bottom: 1em;
  }
  .metric-group.growth .metric-rating-grid:last-child {
    /* For ES/MS/K8 */
    padding-bottom: 0px;
  }
  .metric-group.ctag .sub-heading {
    /* For HST/YABC */
    padding-top: 20px;
  }
  .metric-group.ccr .metric-value-list.no-values {
    display: none;
  }
  .metric-group {
    /deep/ .viz-legend {
      .legend-group .bar.comp {
        height: 0.4em;
      }
    }
  }
}
.tabs-container:not(.is-proj-grad) #tab-content-sa {
  .metric-group.grad /deep/ .grad-rate-disclaimer {
    display: none;
  }
}

.fr-tab-content {
  .metric-group.question{
    .description:not(:last-child){
      padding-bottom: 12px;
    }
  }
  .metric-group.survey {
    .sub-heading {
      padding-bottom: 12px;
    }
  }
  .metric-group {
    &.survey, &.grad, &.ccr, &.prog-to-grad {
      .metric-bignum /deep/ .school-value {
        padding-left: 0;
        text-align: right;
        &.is-na {
          padding-right: 5px;
        }
      }
    }
  }
}
@media print {
  #tab-content-se {
    .survey-viz-2 {
      page-break-inside: avoid;
    }
  }
}

/* .framework-details.st-pre-k {
  &.pk-district {
    #tab-content-ct {
      .metric-group.question {
        display: none;
      }
    }
  }
  &:not(.pk-district):not(.updating-layout) {
    #tab-content-ct {
      .metric-group:not(.question) {
        display: none;
      }
    }
  }
  &:not(.pk-district).updating-layout {
    #tab-content-ct {
      .metric-group:not(.question) {
        visibility: hidden;
      }
    }
  }
} */

.st-es #tab-content-tr .metric-survey-rr {
  /* Add extra padding on bottom only for ES because there are only 3 survey
      questions in TR, so LFM box is taller than that section */
  padding-bottom: 10px;
}

#tab-content-tr .metric-column.col-2 {
  /* For bottom positioning of Footer for printing */
  position: relative;
  .report-footer {
    position: absolute;
    bottom: 0px;
    width: 100%;
    /deep/ .print-only-inline {
      /* Since parent is hidden except for printing, just always show links
        to calculate background size */
      display: inline;
    }
    /deep/ ul {
      /* Table-cell + ul seems to need to manually calculate width,
        subtracting left/right padding + bullet point padding */
      width: $max-width-2-col - 3*24px;
    }
  }
}

.metric-group .description {
  margin-bottom: 0px;
}

.metric-group.lre,
.metric-group.qr-rating {
  .metric-rating-group {
    padding-bottom: 16px;
  }
}
.metric-rating-group.pk-comp {
  padding-bottom: 16px;
}

.metric-group.ctag {
  /deep/ .metric-rating-group .description.rating {
    width: 120px;
    padding-left: 20px;
  }
}

.framework-details.no-qr,
.framework-details.charter {
  /* Don't use display none or size won't update when layout changes */
  &.updating-layout {
    .metric-group.qr-rating {
      visibility: hidden;
    }
  }
  &:not(.updating-layout) {
    .metric-group.qr-rating {
      display: none;
    }
  }
}

.framework-details.no-qr:not(.charter) {
  .metric-group.qr-rating::before {
    display: block;
    content: "Quality Review information is not available";
  }
}

/* Responsive rules */

@media screen and (max-width: $screen-width-2-column-large) {
.metric-column {
  max-width: $max-width-2-col;
}
}
@media screen and (max-width: $screen-width-2-column-small) {
.framework-details {
  /deep/ .tab-content {
    margin: 0px 10px;
  }
  /deep/ .tab-button-bar {
    display: none;
  }
}
/deep/ .tab-print-header.selected {
  display: block;
}
.mobile-view-spacer {
  height: 105px;
}
#tab-content-info {
  .metric-group.gen .description,
  .metric-group.sports .description.metric-colon {
    padding-left: $info-desc-indent;
    text-indent: -$info-desc-indent;
  }
}
}
@media screen and (max-width: $screen-width-1-column-large) {
.element-overall-rating,
.metric-column {
  width: 100%;
  max-width: $max-width-1-col;
  margin: 0px auto;
}
/deep/ .viz-comp-bars {
  max-width: $max-width-2-col;
}
.metric-columns-wrapper {
  display: block;
}
#tab-content-info {
  .metric-group.gen .description,
  .metric-group.sports .description.metric-colon {
    padding-left: 2*$info-desc-indent;
    text-indent: -$info-desc-indent;
  }
}
}
@media screen and (max-width: $screen-width-1-column-medium) {
.framework-details {
  /deep/ .tab-content {
    margin: 0px 10px;
  }
}
}
@media screen and (max-width: $screen-width-1-column-small) {
#tab-content-info {
  .metric-group.gen .description,
  .metric-group.sports .description.metric-colon {
    padding-left: $info-desc-indent;
    text-indent: -$info-desc-indent;
  }
}
}

/* Custom print rules */

@media print {
  .framework-details {
    box-shadow: none;
  }
  .fr-tab-content.print-hide-survey-rr .survey-rr {
    display: none;
  }
  .framework-details.charter .metric-group.qr-rating {
    display: none;
  }
  .section-name {
    padding: 0px 24px;
  }
  /deep/ #tab-content-sa {
    padding-bottom: 0px;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/sqs/2021/FrameworkDetails.vue